import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Grid,
  GridColumn,
  GridNoRecords,
  getSelectedState,
} from "@progress/kendo-react-grid";
import { getter } from "@progress/kendo-react-common";
import { Splitter } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { formatDateForAPI } from "components/common/formatter.js";
import {
  getDocument,
  downloadDocumentAsPDF,
  downloadDocument,
  getDocumentEditor,
  getMatterDocDuplicate,
  updateMatterActivity,
  downloadDocumentsAsZip,
} from "api/index";
import { getAccessRightsData } from "services/currentUserService";

import DocumentViewer from "components/common/DocumentViewer";
import { fileIcon } from "components/common/fileIcon";
import { HandleGridBody } from "components/common/HandleGridBody";
import { getActiveActivityTypes } from "services/activityTypeService";
import { useCurrentUser } from "services/currentUserService";
import { ComboBox, DropDownList } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { chevronDownIcon } from "@progress/kendo-svg-icons";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useDoubleClick } from "components/common/hooks/SingleDoubleClick";
import { Button } from "@progress/kendo-react-buttons";
import { arrowRotateCwIcon as refreshIcon } from "@progress/kendo-svg-icons";
import SearchTextBox from "components/SearchComponent";
import { FaFilter } from "react-icons/fa";
import filtersAppliedIcon from "../../assets/CaseFileIcons/FilterApplied.svg";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import styles from "./WorkDone.module.scss";
import MenuCell from "../CaseFile/CaseActivities/MenuCell";
import ShareDocumentModal from "../CaseFile/ShareDocumentModal";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import ESigning from "../CaseFile/ESigning";
import CaseActivityForm from "../CaseFile/CaseActivities/CaseActivityForm";
import { toast } from "react-toastify";
import ActionbarSkelton from "components/common/Loaders/ActionbarSkelton";
import { GRID_PAGE_SIZES } from "components/common/GridComponents";
import { FilterComponent } from "components/common/FiltersComponent";

const DEFAULT_PANES = [
  {
    collapsible: true,
  },
  {
    size: "0px",
  },
];

const DATA_ITEM_KEY = "activityId";

const WorkDone = () => {
  const getDynamicDocOpenPanes = () => [
    {
      collapsible: true,
    },
    {
      size: "40%",
      min: "500px",
      collapsible: false,
    },
  ];
  const idGetter = getter(DATA_ITEM_KEY);
  const { data: user } = useCurrentUser();
  const searchRef = useRef(null);
  const filterAnchorRef = useRef(null);
  const filterContentRef = useRef(null);
  const filterPopupRef = useRef(null);

  const [activityTypes, setActivityTypes] = useState([]);
  const [selectedState, setSelectedState] = useState({});
  const [paneDocument, setPaneDocument] = useState([]);
  const [panes, setPanes] = useState(DEFAULT_PANES);
  const [loader, setLoader] = useState(true);
  const [currentMatterContext, setCurrentMatterContext] = useState({});
  const [updateFilter, setUpdateFilter] = useState(false);
  const [typeID, setTypeID] = useState({ name: "ALL", activityTypeID: "" });
  const [loaded, setLoaded] = useState(false);
  const [allowAccessData, setAllowAccessData] = useState({});
  const [filterClicked, setFilterClicked] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);
  const [actionBarLoading, setActionBarLoading] = useState(true);

  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const INITIAL_FILTER_STATE = {
    branchId: {
      branchName: "ALL",
      branchId: -2,
    },
    teamId: { teamName: "ALL", teamID: -2 },
    feeEarnerRef: {
      display: user?.display || "",
      userRef: user?.userRef || "",
    },
  };

  const [filterState, setFilterState] = useState(INITIAL_FILTER_STATE);
  const [fromDate, setFromDate] = useState(startOfMonth);
  const [toDate, setToDate] = useState(today);
  const [fromDateChecked, setFromDateChecked] = useState(true);
  const [toDateChecked, setToDateChecked] = useState(false);

  // Temporary state for popup filter (only used when popup is open)
  const [tempFromDate, setTempFromDate] = useState(startOfMonth);
  const [tempToDate, setTempToDate] = useState(today);
  const [tempFromDateChecked, setTempFromDateChecked] = useState(true);
  const [tempToDateChecked, setTempToDateChecked] = useState(false);
  const [tempTypeID, setTempTypeID] = useState({
    name: "ALL",
    activityTypeID: "",
  });
  const [searchValue, setSearchValue] = useState("");
  const [isRefreshed, setIsRefreshed] = useState(false);
  const [list, setLists] = useState({
    data: [],
    total: 0,
  });

  const [viewActivity, setViewActivity] = useState();
  const [isView, setView] = useState(false);
  const [multiSel, setMultiSel] = useState(false);
  const [showShareDocument, setShowShareDocument] = useState(false);
  const [currentDoc, setCurrentDoc] = useState({});
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [eSigning, setESigning] = useState();
  const [addActivity, setAddActivity] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [droppedFilesActivity, setDroppedFilesActivity] = useState([]);

  const createState = (skip, take) => {
    let pagerSettings = {
      pageSizes: GRID_PAGE_SIZES,
    };
    return {
      pageSize: take,
      pageable: pagerSettings,
    };
  };

  const [state] = useState(createState(0, 200));

  const [dataState, setDataState] = useState({
    take: 200,
    skip: 0,
    sort: [
      {
        field: "date",
        dir: "desc",
      },
    ],
  });

  const SELECTED_FIELD = "selected";

  useEffect(() => {
    if (user && allowAccessData.branches) {
      const userBranch = allowAccessData.branches?.find((branch) =>
        branch.teams?.some((team) =>
          team.feeEarners?.some((fe) => fe.userRef === user.userRef),
        ),
      );

      const userTeam = userBranch?.teams?.find((team) =>
        team.feeEarners?.some((fe) => fe.userRef === user.userRef),
      );

      setFilterState({
        branchId: {
          branchName: allowAccessData.accessAllBranches
            ? "All"
            : userBranch?.branchName || "ALL",
          branchId: allowAccessData.accessAllBranches
            ? -2
            : userBranch?.branchId || -2,
        },
        teamId: {
          teamName: allowAccessData.accessAllTeams
            ? "All"
            : userTeam?.teamName || "ALL",
          teamID: allowAccessData.accessAllTeams ? -2 : userTeam?.teamID || -2,
        },
        feeEarnerRef: {
          display: user?.display || "",
          userRef: user?.userRef || "",
        },
      });
    }
  }, [user, allowAccessData]);

  useEffect(() => {
    if (updateFilter) {
      setUpdateFilter(false);
    }
  }, [updateFilter]);

  useEffect(() => {
    if (!filterClicked) return;

    const handlePointerDown = (event) => {
      const anchorElement = filterAnchorRef.current;
      if (anchorElement && anchorElement.contains(event.target)) {
        return;
      }

      if (event.target?.closest?.(".k-calendar")) {
        return;
      }

      const popupElement = filterPopupRef.current;
      const popupContainer = popupElement
        ? popupElement.closest(".k-animation-container")
        : null;
      const rect =
        popupContainer?.getBoundingClientRect?.() ||
        popupElement?.getBoundingClientRect?.();

      if (!rect) {
        setFilterClicked(false);
        return;
      }

      const { clientX, clientY } = event;
      const isInside =
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom;
      if (!isInside) {
        setFilterClicked(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [filterClicked]);

  const onOpen = () => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setTempFromDateChecked(fromDateChecked);
    setTempToDateChecked(toDateChecked);
    setTempTypeID(typeID);
  };

  const getAccessRights = async () => {
    try {
      const data = await getAccessRightsData();
      setAllowAccessData(data?.data?.data || {});
    } catch (error) {}
  };

  const fetchActivityTypes = async () => {
    try {
      const types = await getActiveActivityTypes();
      setActivityTypes(types || []);
    } catch (error) {}
  };

  useEffect(() => {
    const loadActionBarData = async () => {
      setActionBarLoading(true);
      try {
        await Promise.all([getAccessRights(), fetchActivityTypes()]);
      } finally {
        setActionBarLoading(false);
      }
    };
    loadActionBarData();
  }, []);

  const showPaneFile = async (id, matterRef, matterId) => {
    setLoaded(false);
    try {
      if (!id) {
        setPaneDocument([]);
        setPanes(getDynamicDocOpenPanes());
        setCurrentMatterContext({});
        return;
      }
      const { data } = await getDocument(id);
      setPaneDocument([data.data]);
      setCurrentMatterContext({
        matterRef: matterRef || "",
        matterId: matterId || null,
      });
      setPanes(getDynamicDocOpenPanes());
    } catch (error) {
      setPaneDocument([]);
      setPanes(getDynamicDocOpenPanes());
      setCurrentMatterContext({});
    } finally {
      setLoaded(true);
    }
  };

  const showDocumentFile = (documentId, matterRef, matterId) => {
    if (!documentId) return;

    const encodedId = btoa(
      JSON.stringify({
        id: documentId,
        matterRef: matterRef || "",
        matterId: matterId || null,
      }),
    );

    const url = `/sa/document-viewer?id=${encodedId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownload = async (documentId, asPDF = false, fromDocView) => {
    setLoader(true);
    try {
      const selectedDocumentIds = Object.keys(selectedState)
        .filter((key) => selectedState[key])
        .map((key) => {
          const selectedItem = list?.data?.find((item) => item.activityId === key);
          return selectedItem?.documentId;
        })
        .filter((id) => id);

      let response;
      let fileName;

      if (selectedDocumentIds.length > 1 && !fromDocView) {
        fileName = "work-done-documents.zip";
        response = await downloadDocumentsAsZip(fileName, selectedDocumentIds);
      } else if (selectedDocumentIds.length === 1 || documentId) {
        response = asPDF
          ? await downloadDocumentAsPDF(documentId || selectedDocumentIds[0])
          : await downloadDocument(documentId || selectedDocumentIds[0]);

        const contentDisposition = response.headers["content-disposition"];

        if (response.data?.filename) {
          fileName = response.data.filename;
        } else if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
          );
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1].replace(/['"]/g, "");
          }
        }
        if (!fileName) {
          fileName =
            (response.data?.fileID || "downloaded-document") + (asPDF ? ".pdf" : "");
        }
      } else {
        setLoader(false);
        return;
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const tempLink = document.createElement("a");
      tempLink.href = url;
      tempLink.setAttribute("download", fileName);
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(url);
    } catch (error) {} finally {
      setLoader(false);
    }
  };

  const handleDuplicate = async (matterId, activityId) => {
    try {
      const { data } = await getMatterDocDuplicate(matterId, activityId);
      const activityData = data?.data;

      setCurrentMatterContext({
        matterRef: activityData?.matterRef,
        matterId: activityData?.matterId || matterId,
      });

      setViewActivity(activityData);
      setView(true);
      setIsDuplicate(true);
      setAddActivity(true);
    } catch (error) {}
  };

  const getDocumentEditorData = async (id, type) => {
    try {
      setLoader(true);
      const { data } = await getDocumentEditor(id, currentMatterContext.matterId);
      let editUrl = data?.data?.editURL;
      if (type === "Local") {
        let ext = "";
        if (paneDocument && paneDocument[0]) {
          const doc = paneDocument[0];
          if (doc.documentName) {
            ext = doc.documentName.split(".").pop().toLowerCase();
          }
        }
        if (["xls", "xlsx"].includes(ext)) {
          editUrl = `ms-excel:ofe|u|${editUrl}`;
        } else {
          editUrl = `ms-word:ofe|u|${editUrl}`;
        }
      }
      window.open(editUrl, "_blank");
      showPaneFile(
        id,
        currentMatterContext.matterRef,
        currentMatterContext.matterId,
      );
    } catch (error) {
      const statusCode = error?.response?.status;
      if (statusCode !== 460) {
      }
    } finally {
      setLoader(false);
    }
  };

  const handleActivityRowClick = (activity) => {
    setSelectedState({
      [activity.activityId]: true,
    });
    // Set the current matter context from the activity
    setCurrentMatterContext({
      matterRef: activity.matterRef,
      matterId: activity.matterId,
    });
    setViewActivity(activity);
    setView(true);
    setAddActivity(true);
  };

  const setCurrentMatterForShare = (matterContext) => {
    setCurrentMatterContext(matterContext);
  };

  const getActivitiesData = () => {
    handleRefresh();
  };

  const deleteMatterActivity = async (data) => {
    try {
      setLoader(true);
      await updateMatterActivity(data.matterId, data.activityId, {
        ...data,
        active: false,
      });
      setDeleteConfirmation(false);
      toast.success("Activity deleted successfully");
      handleRefresh();
    } catch (error) {} finally {
      setLoader(false);
    }
  };

  const onChange = (event) => {
    if (event?.newState[0].collapsed) {
      setPanes([
        {
          ...event?.newState[0],
        },
        {},
      ]);
      return;
    } else if (!event?.newState[1].collapsed && !event?.newState[1].size) {
      setPanes(getDynamicDocOpenPanes());
      return;
    } else {
      setPanes([...event.newState]);
    }
  };

  const onSelectionChange = useCallback(
    (event) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: DATA_ITEM_KEY,
      });
      setMultiSel(true);
      setSelectedState(newSelectedState);

      // Show document for the first selected item
      const firstSelectedKey = Object.keys(newSelectedState)[0];
      if (firstSelectedKey) {
        const selectedItem = list?.data?.find(
          (item) => item.activityId == firstSelectedKey,
        );
        if (selectedItem?.documentId) {
          showPaneFile(
            selectedItem.documentId,
            selectedItem.matterRef,
            selectedItem.matterId,
          );
        } else {
          setPaneDocument([]);
          setPanes(getDynamicDocOpenPanes());
          setCurrentMatterContext({});
        }
      } else {
        setPaneDocument([]);
        setPanes(getDynamicDocOpenPanes());
        setCurrentMatterContext({});
      }
    },
    [selectedState, list?.data],
  );

  const onHeaderSelectionChange = useCallback(
    (event) => {
      const checkboxElement = event.syntheticEvent.target;
      const checked = checkboxElement.checked;
      const newSelectedState = {};
      event.dataItems.forEach((item) => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedState(newSelectedState);
    },
    [idGetter],
  );

  const dataStateChange = (e) => {
    if (
      e.dataState.sort !== undefined &&
      e.dataState.skip !== 0 &&
      dataState.sort !== e.dataState.sort
    ) {
      setDataState({ ...e.dataState, skip: 0 });
    } else if (e.dataState.sort && e.dataState.sort[0] === undefined) {
      setDataState({
        ...e.dataState,
        sort: [
          {
            field: dataState.sort[0].field,
            dir: "asc",
          },
        ],
      });
    } else {
      setDataState(e.dataState);
    }
    setUpdateFilter(true);
  };

  const TypeCell = (prop) => {
    const handleTypeClick = (e) => {
      e.stopPropagation();
      handleActivityRowClick(prop.dataItem);
    };

    return (
      <td className="fs-sm wrap-text">
        <u style={{ cursor: "pointer" }} onClick={handleTypeClick}>
          {prop.dataItem.type}
        </u>
      </td>
    );
  };

  const DateCell = (props) => {
    const dateField = props.dataItem[props.field];

    if (dateField) {
      const dt = new Date(dateField);
      const dateStr = dt.toLocaleDateString("en-GB");
      const timeStr = dt.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      return (
        <td
          onClick={(e) => {
            e.stopPropagation();
            setSelectedState({
              [props.dataItem.activityId]: true,
            });
            showPaneFile(
              props.dataItem?.documentId,
              props.dataItem?.matterRef,
              props.dataItem?.matterId,
            );
          }}
          className={`fs-sm ${props.className}`}>
          <div className="text-nowrap">{dateStr}</div>
          <div className="text-nowrap">{timeStr}</div>
        </td>
      );
    } else {
      return <td></td>;
    }
  };

  function truncateFileName(fileName) {
    if (fileName) {
      const extensionIndex = fileName.lastIndexOf(".");

      if (extensionIndex === -1) {
        return fileName.length > 8 ? fileName.substring(0, 18) : fileName;
      }

      const extension = fileName.substring(extensionIndex);

      if (fileName.length > 8) {
        return fileName.substring(0, 7 - extension.length) + extension;
      }

      return fileName;
    }
  }

  const DocumentCell = (e) => {
    const { documentId, documentName, matterRef, matterId } = e.dataItem;

    const hybridClick = useDoubleClick(
      () => showDocumentFile(documentId, matterRef, matterId),
      () => {
        setSelectedState({
          [e.dataItem.activityId]: true,
        });
        showPaneFile(documentId, matterRef, matterId);
      },
    );

    return documentId ? (
      <td className={e.className ?? ""} style={e.style} onClick={hybridClick}>
        <div role="button" className="d-flex">
          {fileIcon(documentName)}
          <span className="ps-2 fs-sm d-lg-none d-xl-block">
            {truncateFileName(documentName)}
          </span>
          <span className="ps-2 fs-sm d-xl-none d-lg-block">
            {documentName?.split(".").pop()}
          </span>
        </div>
      </td>
    ) : (
      <td />
    );
  };

  const EmailCell = (props) => {
    const { emailFrom, emailTo, documentId, activityId, matterRef, matterId } =
      props.dataItem;

    return (
      <td
        className={`fs-sm wrap-text ${props.className}`}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedState({
            [activityId]: true,
          });
          showPaneFile(documentId, matterRef, matterId);
        }}>
        <div className="text-nowrap">{emailFrom}</div>
        <div className="text-nowrap">{emailTo}</div>
      </td>
    );
  };

  const MatterRefCell = (prop) => {
    return <td className="fs-sm">{prop.dataItem.matterRef || ""}</td>;
  };

  const dataReceived = (data) => {
    if (data) {
      setLists(data);
      setLoader(false);
    }
  };

  const handleRefresh = () => {
    setDataState({
      ...dataState,
      skip: 0,
    });
    setSelectedState({});
    setPaneDocument([]);
    setPanes(DEFAULT_PANES);
    setCurrentMatterContext({});
    setIsRefreshed(!isRefreshed);
    setUpdateFilter(true);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value || "");
    setUpdateFilter(true);
  };

  const updateFilterValues = () => {
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setFromDateChecked(tempFromDateChecked);
    setToDateChecked(tempToDateChecked);
    setTypeID(tempTypeID);

    setUpdateFilter(true);
    setFilterClicked(false);
  };

  useEffect(() => {
    const isFromDateDifferent =
      fromDateChecked &&
      fromDate &&
      (fromDate.getFullYear() !== startOfMonth.getFullYear() ||
        fromDate.getMonth() !== startOfMonth.getMonth() ||
        fromDate.getDate() !== startOfMonth.getDate());

    const isToDateSet = toDateChecked && toDate !== null;

    const isTypeSet =
      typeID?.activityTypeID !== "" && typeID?.activityTypeID !== undefined;

    const filterStatus = isFromDateDifferent || isToDateSet || isTypeSet;
    setFilterApplied(filterStatus);
  }, [fromDate, toDate, fromDateChecked, toDateChecked, typeID, startOfMonth]);

  const handleFilterReset = () => {
    const resetDate = startOfMonth;
    const resetType = { name: "ALL", activityTypeID: "" };

    // Reset both actual and temporary values
    setFromDate(resetDate);
    setToDate(today);
    setFromDateChecked(true);
    setToDateChecked(false);
    setTypeID(resetType);
    setTempFromDate(resetDate);
    setTempToDate(today);
    setTempFromDateChecked(true);
    setTempToDateChecked(false);
    setTempTypeID(resetType);
    setUpdateFilter(true);
  };

  const mappedData = {
    data: list?.data?.map((item) => ({
      ...item,
      [SELECTED_FIELD]: selectedState[idGetter(item)],
    })),
    total: list?.total,
  };

  const getRowStyle = (dataItem) => {
    const activityType = activityTypes.find(
      (type) => type.activityTypeID === dataItem.typeId,
    );

    if (
      activityType &&
      activityType.rowStyle &&
      typeof activityType.rowStyle === "string"
    ) {
      const match = activityType.rowStyle.match(/backgroundColor:['"]([^'"]+)['"]/);
      if (match && match[1]) {
        return { backgroundColor: match[1] };
      }
    }

    return {};
  };

  return (
    <div className="activity-grid d-flex flex-column h-100 px-4">
      {actionBarLoading ? (
        <ActionbarSkelton length={3} />
      ) : (
        <div
          className={`d-flex align-items-end gap-2 py-3 justify-content-between ${styles.actionBarRow}`}>
          <div className={`d-flex gap-2 align-items-end ${styles.actionBarFilters}`}>
            <FilterComponent
              allowAccessData={allowAccessData}
              inlineLayout={true}
              disabledFeeEarner={false}
              updateFilters={(filters) => {
                setFilterState({
                  branchId: { 
                    branchName: allowAccessData.branches?.find(b => b.branchId === filters.branch)?.branchName || "ALL",
                    branchId: filters.branch 
                  },
                  teamId: { 
                    teamName: allowAccessData.branches?.find(b => b.branchId === filters.branch)?.teams?.find(t => t.teamID === filters.teamId)?.teamName || "ALL",
                    teamID: filters.teamId 
                  },
                  feeEarnerRef: {
                    display: filters.feeEarnerRef === "ALL" ? "ALL" : 
                      allowAccessData.branches?.find(b => b.branchId === filters.branch)?.teams?.find(t => t.teamID === filters.teamId)?.feeEarners?.find(fe => fe.userRef === filters.feeEarnerRef)?.display || filters.feeEarnerRef,
                    userRef: filters.feeEarnerRef
                  }
                });
                setUpdateFilter(true);
              }}
            />
            <div className={`gap-2 align-items-end ${styles.inlineFiltersWrap}`}>
              <div className={styles.filterItem}>
                <Label>Activity Type</Label>
                <ComboBox
                  style={{ maxWidth: "180px", minWidth: "150px" }}
                  data={activityTypes}
                  textField="name"
                  dataItemKey="activityTypeID"
                  value={typeID}
                  svgIcon={chevronDownIcon}
                  onChange={(e) => {
                    setTypeID(e.value || { name: "ALL", activityTypeID: "" });
                    setUpdateFilter(true);
                  }}
                />
              </div>

              <div className={styles.filterItem}>
                <div>
                  <Label editorId={"from"} className="me-5 pe-5">
                    From
                  </Label>
                  <Label editorId={"to"} className="ms-4 ps-1">
                    To
                  </Label>
                </div>
                <div className="date-form-to-wrapper">
                  <Checkbox
                    className="ms-2 mb-auto"
                    value={fromDateChecked}
                    onChange={(e) => {
                      setFromDateChecked(e.value);
                      fromDate && setUpdateFilter(true);
                    }}
                  />
                  <div className="from">
                    <DatePicker
                      id="from"
                      className="date-picker pe-2"
                      format="dd/MM/yyyy"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.value);
                        setFromDateChecked(true);
                        setUpdateFilter(true);
                      }}
                      toggleButton={CalenderCustomToggleButton}
                    />
                  </div>
                  <div className="divider m-1"></div>
                  <Checkbox
                    className="ms-2 mt-auto mb-auto"
                    value={toDateChecked}
                    onChange={(e) => {
                      setToDateChecked(e.value);
                      toDate && setUpdateFilter(true);
                    }}
                  />
                  <div className="to">
                    <DatePicker
                      id="to"
                      className="date-picker pe-2"
                      format="dd/MM/yyyy"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.value);
                        setToDateChecked(true);
                        setUpdateFilter(true);
                      }}
                      toggleButton={CalenderCustomToggleButton}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex gap-1 align-items-end">
            <div
              style={{
                flex: "1 1 0%",
                minWidth: "180px",
                maxWidth: "260px",
              }}>
              <SearchTextBox
                width="100%"
                height="40px"
                ref={searchRef}
                changeFilterValue={handleSearchChange}
              />
            </div>
            <div
              ref={filterAnchorRef}
              onClick={() => setFilterClicked(!filterClicked)}
              className={styles.filterBtnWrap}>
              {!filterApplied ? (
                <Button className={`common-btn ${styles.customBtn}`}>
                  <FaFilter color="white" size={16} />
                </Button>
              ) : (
                <Button className={`common-btn ${styles.customBtn}`}>
                  <img
                    src={filtersAppliedIcon}
                    alt="Filters Applied"
                    width={25}
                    height={25}
                  />
                </Button>
              )}
            </div>
            <Button
              className={`common-btn  ${styles.customBtn}`}
              svgIcon={refreshIcon}
              onClick={handleRefresh}
              title="Refresh"
            />
          </div>
        </div>
      )}
      <div className="h-100 flex-grow-1" style={{ minHeight: 0 }}>
        <Splitter
          panes={panes}
          onChange={onChange}
          onBarToggle={() => {
            if (panes[0].collapsed) {
              setPanes(getDynamicDocOpenPanes());
            } else {
              setPanes([
                {
                  collapsed: true,
                },
                {},
              ]);
            }
          }}
          className={`workflow-splitter border-0 h-100 ${
            Object.keys(selectedState).length === 0 && styles["hide-splitbar"]
          }`}>
          <Grid
            {...dataState}
            data={mappedData}
            sortable={true}
            dataItemKey={DATA_ITEM_KEY}
            selectedField={SELECTED_FIELD}
            selectable={{
              enabled: true,
              drag: false,
              cell: false,
              mode: "multiple",
            }}
            onDataStateChange={dataStateChange}
            onSelectionChange={onSelectionChange}
            onHeaderSelectionChange={onHeaderSelectionChange}
            className="h-100 overflow-auto grid-row-select casefile_outlook_grid grid-minwidth-col6-150"
            pageable={state.pageable}
            pageSize={state.pageSize}
            rowRender={(row, props) => {
              const style = getRowStyle(props.dataItem);
              return React.cloneElement(row, {
                style: { ...row.props.style, ...style },
              });
            }}>
            <GridNoRecords>
              {loader ? (
                <div style={{ height: "100px" }}></div>
              ) : (
                mappedData.data &&
                mappedData.data.length === 0 &&
                "No record available"
              )}
            </GridNoRecords>
            <GridColumn
              field={SELECTED_FIELD}
              width="50px"
              headerCell={() => <></>}
              headerSelectionValue={
                mappedData?.data?.findIndex(
                  (item) => !selectedState[idGetter(item)],
                ) === -1
              }
            />
            <GridColumn
              field="type"
              title="Type"
              cell={TypeCell}
              width="120px"
              className="fs-sm"
            />
            <GridColumn
              field="date"
              title="Date"
              cell={DateCell}
              width="100px"
              className="p-1 cursor-default"
            />
            <GridColumn
              field="fE"
              title="FE"
              width="60px"
              className="cursor-default"
            />
            <GridColumn
              field="matterRef"
              title="Matter Ref"
              cell={MatterRefCell}
              width="120px"
            />
            <GridColumn field="details" title="Details" className="cursor-default" />
            <GridColumn
              field=""
              title=" "
              cell={(prop) => (
                <MenuCell
                  {...prop}
                  loaded={loaded}
                  setLoaded={setLoaded}
                  setSelectedState={setSelectedState}
                  selectedState={selectedState}
                  showPaneFile={showPaneFile}
                  multiSel={multiSel}
                  setMultiSel={setMultiSel}
                  setAddActivity={setAddActivity}
                  setViewActivity={setViewActivity}
                  setView={setView}
                  handleActivityRowClick={handleActivityRowClick}
                  showDocumentFile={showDocumentFile}
                  handleDownload={handleDownload}
                  getDocumentEditorData={getDocumentEditorData}
                  handleDuplicate={handleDuplicate}
                  setShowShareDocument={setShowShareDocument}
                  setCurrentDoc={setCurrentDoc}
                  setDeleteConfirmation={setDeleteConfirmation}
                  setESigning={setESigning}
                  setCurrentMatterForShare={setCurrentMatterForShare}
                  matterId={prop?.dataItem?.matterId}
                />
              )}
              width="40px"
            />
            <GridColumn
              title="Documents"
              cell={DocumentCell}
              width="120px"
              className="fs-sm px-1"
            />
            <GridColumn
              title="Email"
              cell={EmailCell}
              width="200px"
              className="fs-sm px-1 cursor-default"
            />
          </Grid>

          <div className="h-100">
            <DocumentViewer
              loader={loaded}
              onDownload={handleDownload}
              data={paneDocument}
              onRefresh={(id) =>
                showPaneFile(
                  id,
                  currentMatterContext.matterRef,
                  currentMatterContext.matterId,
                )
              }
              onScreenComponent={true}
              onClose={() => {
                setPanes(DEFAULT_PANES);
                setPaneDocument([]);
                setSelectedState({});
                setCurrentMatterContext({});
              }}
              onExpand={() => {
                if (panes[0].collapsed) {
                  setPanes(getDynamicDocOpenPanes());
                } else {
                  setPanes([
                    {
                      collapsed: true,
                    },
                    {},
                  ]);
                }
              }}
              onOpenInNewTab={(documentId) =>
                showDocumentFile(
                  documentId,
                  currentMatterContext.matterRef,
                  currentMatterContext.matterId,
                )
              }
              matterId={currentMatterContext.matterId}
              matter={currentMatterContext}
              component="workDone"
            />
          </div>
        </Splitter>
      </div>

      <Popup
        key={filterClicked ? "open" : "closed"}
        show={filterClicked}
        anchor={filterAnchorRef?.current}
        anchorAlign={{
          horizontal: "right",
          vertical: "bottom",
        }}
        popupAlign={{
          horizontal: "right",
          vertical: "top",
        }}
        onOpen={onOpen}>
        <div
          ref={(el) => {
            filterPopupRef.current = el;
            filterContentRef.current = el;
          }}
          role="menu"
          className="d-flex flex-column align-items-center">
          <div className="d-flex align-items-start flex-column p-2 mx-2">
            <Label className="mt-2">Set Filters</Label>
            <hr className="w-100 mt-2 " />
            <div style={{ width: "200px" }}>
              <div>
                <Label editorId="from" className="me-5 pe-5 mb-1">
                  From
                </Label>
              </div>
              <div className="date-form-to-wrapper">
                <Checkbox
                  className="ms-2 mt-auto mb-auto"
                  value={tempFromDateChecked}
                  onChange={(e) => {
                    setTempFromDateChecked(e.value);
                  }}
                />
                <div className="from">
                  <DatePicker
                    id="from"
                    className="date-picker margin-date"
                    format="dd/MM/yyyy"
                    value={tempFromDate}
                    onChange={(e) => {
                      setTempFromDate(e.value);
                      setTempFromDateChecked(true);
                    }}
                    toggleButton={CalenderCustomToggleButton}
                  />
                </div>
              </div>
              <div className="mt-2">
                <Label editorId="to" className=" mb-1">
                  To
                </Label>
                <div className="date-form-to-wrapper">
                  <Checkbox
                    className="ms-2 mt-auto mb-auto"
                    value={tempToDateChecked}
                    onChange={(e) => {
                      setTempToDateChecked(e.value);
                    }}
                  />
                  <div className="to">
                    <DatePicker
                      id="to"
                      className="date-picker margin-date "
                      format="dd/MM/yyyy"
                      value={tempToDate}
                      onChange={(e) => {
                        setTempToDate(e.value);
                        setTempToDateChecked(true);
                      }}
                      toggleButton={CalenderCustomToggleButton}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="my-2 d-flex flex-column ">
              <Label className="label mb-1">Type</Label>
              <DropDownList
                className="dropdown me-2"
                style={{ width: "200px" }}
                svgIcon={chevronDownIcon}
                textField="name"
                name="name"
                data={activityTypes}
                value={tempTypeID}
                onChange={(e) => {
                  setTempTypeID(e.value || { name: "ALL", activityTypeID: "" });
                }}
              />
            </div>

            <div className="d-flex my-2">
              <Button
                className="common-btn bgsecondary me-2 text-dark"
                onClick={handleFilterReset}>
                Reset
              </Button>
              <Button className="common-btn " onClick={updateFilterValues}>
                Update
              </Button>
            </div>
          </div>
        </div>
      </Popup>

      <HandleGridBody
        dataState={dataState}
        currentComponent="workDone"
        branchId={filterState.branchId?.branchId}
        teamId={filterState.teamId?.teamID}
        feeEarnerRef={filterState.feeEarnerRef?.userRef}
        fD={fromDateChecked && fromDate ? formatDateForAPI(fromDate) : null}
        tD={toDateChecked && toDate ? formatDateForAPI(toDate) : null}
        typeId={typeID.activityTypeID}
        onDataReceived={dataReceived}
        searchValue={searchValue}
        updateFilter={updateFilter}
        refresh={isRefreshed}
      />

      {showShareDocument && (
        <ShareDocumentModal
          onClose={() => setShowShareDocument(false)}
          selectedDocuments={Object.keys(selectedState)
            .filter((key) => selectedState[key])
            .map((key) => {
              const selectedItem = list?.data?.find(
                (item) => item.activityId === key,
              );
              return {
                id: selectedItem?.documentId,
                name: selectedItem?.documentName,
              };
            })
            .filter((item) => item.id && item.name)}
          matterId={currentMatterContext?.matterId}
          matter={currentMatterContext}
          currentDoc={currentDoc}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationDialog
          content={`Are you sure you want to delete activity '${deleteConfirmation?.details}'?`}
          onClickYes={() => {
            deleteMatterActivity(deleteConfirmation);
          }}
          onClickNo={() => setDeleteConfirmation(false)}
          onClose={() => setDeleteConfirmation(false)}
        />
      )}

      {eSigning && (
        <ESigning
          matterId={currentMatterContext?.matterId}
          title={"Select Contacts"}
          activityId={eSigning}
          onClose={() => setESigning()}
        />
      )}

      {addActivity && (
        <CaseActivityForm
          getActivitiesData={getActivitiesData}
          addFormText={`${currentMatterContext?.matterRef || ""} - Work Done`}
          droppedFilesActivity={droppedFilesActivity}
          viewActivity={viewActivity}
          feeEarnerRef={viewActivity?.fE || user?.userRef}
          matterDetails={{
            matterRef: currentMatterContext?.matterRef,
            matterId: currentMatterContext?.matterId,
            feeEarnerRef: viewActivity?.fE || user?.userRef,
          }}
          isView={isView}
          isDuplicate={isDuplicate}
          reference={currentMatterContext?.matterId}
          onMatterRefClickActions={() => {}}
          setshowMatterForm={() => {
            setViewActivity();
            setView(false);
            setDroppedFilesActivity([]);
            if (isDuplicate) setIsDuplicate(false);
            setAddActivity(false);
            setCurrentMatterContext({});
          }}
          isAddActivityBtn={false}
        />
      )}
    </div>
  );
};

export default WorkDone;
